import dayjs from 'dayjs'
import { TimeRange } from 'features/systemLens'
import type { PointAnnotation } from 'features/timeSeries/components/pointTool/usePointAnnotationTool'
import type { Ruler } from 'features/timeSeries/components/rulerTool/useRulerTool'
import type { ActiveSentinel } from 'features/timeSeries/hooks/useSentinelScores'
import {
  BaseSensorEntry,
  ChartTimeUnit,
  DateMode,
  RegressionConfig,
  ResampleConfig,
  SmoothingConfig,
  StandaloneChart,
  ThresholdFilterConfig,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

export const loadSensorsFromChart = (
  chart: StandaloneChart,
  sensorEntries: BaseSensorEntry[]
): BaseSensorEntry[] => {
  const sensorIdsFromChart = (chart.sourceConfigs ?? [])
    .filter((sc) => sc.type === 'sensor')
    .map((sc) => sc.sourceId)

  if (sensorIdsFromChart.length > 0) {
    return sensorEntries.filter((se) => sensorIdsFromChart.includes(se.id))
  }
  return []
}

export const loadDerivedValuesFromChart = (
  chart: StandaloneChart,
  derivedValueEntries: TimeSeriesDerivedValueEntryJSON[]
): TimeSeriesDerivedValueEntryJSON[] => {
  const derivedValueIdsFromChart = (chart.sourceConfigs ?? [])
    .filter((sc) => sc.type === 'derivedValue')
    .map((sc) => sc.sourceId)

  if (derivedValueIdsFromChart.length > 0 && derivedValueEntries.length > 0) {
    return derivedValueEntries.filter(
      (dv) =>
        derivedValueIdsFromChart.includes(dv.id) &&
        dv.data?.dependencyIds &&
        dv.data.dependencyIds.length > 0
    )
  }
  return []
}

export const loadSourceConfigProperties = (chart: StandaloneChart) => {
  const colors: Record<string, string> = {}
  const visibility: Record<string, boolean> = {}
  const axisRanges: Record<string, { min?: number; max?: number }> = {}
  let hasCustomRanges = false

  chart.sourceConfigs.forEach((sc) => {
    // Sentinel configs are keyed by sentinel ENTRY id, not a pen id; their
    // color/hidden state lives in sentinel state (`loadSentinelsFromChart`),
    // so they must not leak entry-id keys into the pen records here.
    if (sc.type === 'sentinel') {
      return
    }
    if (sc.color) {
      colors[sc.sourceId] = sc.color
    }
    if (sc.hidden) {
      visibility[sc.sourceId] = false
    }
    if (sc.yAxisRange) {
      // Saved bounds load VERBATIM. Rounding here (formerly toPrecision(4))
      // made the loaded state differ from the saved entry, so every surface
      // without local state showed a phantom unsaved-changes dot and silently
      // drifted exact typed values. Display formatting belongs at render.
      axisRanges[sc.sourceId] = {
        min: sc.yAxisRange.min,
        max: sc.yAxisRange.max,
      }
      hasCustomRanges = true
    }
  })

  return { colors, visibility, axisRanges, hasCustomRanges }
}

export interface LoadedSentinelState {
  activeSentinels: ActiveSentinel[]
  /** Per-sentinel overlay visibility keyed by entry id; false = hidden. */
  sentinelVisibility: Record<string, boolean>
}

/**
 * Inverse of the sentinel branch of `buildSourceConfigsFromState`: rebuild
 * `activeSentinels` (+ chosen method + overlay visibility) from `'sentinel'`
 * source configs.
 *
 * Deliberately does NOT resolve or re-add the target pen — it is persisted
 * separately as its own sensor config and restored by `loadSensorsFromChart`.
 * Old charts predate the `'sentinel'` type entirely, so a chart without such
 * configs loads as "no sentinels" rather than throwing; a malformed config
 * missing its target is skipped, never crashed on.
 */
export const loadSentinelsFromChart = (
  chart: StandaloneChart
): LoadedSentinelState => {
  const activeSentinels: ActiveSentinel[] = []
  const sentinelVisibility: Record<string, boolean> = {}

  for (const sc of chart.sourceConfigs ?? []) {
    if (sc.type !== 'sentinel') continue
    // A sentinel with no target cannot band anything — drop it rather than
    // rebuild an overlay that could never render.
    if (!sc.sentinelTarget) continue
    activeSentinels.push({
      entryId: sc.sourceId,
      target: sc.sentinelTarget,
      name: sc.sentinelName ?? undefined,
      method: sc.sentinelMethod ?? undefined,
    })
    if (sc.hidden) {
      sentinelVisibility[sc.sourceId] = false
    }
  }

  return { activeSentinels, sentinelVisibility }
}

export interface LoadedDataProcessingConfigs {
  thresholdFilters: Record<string, ThresholdFilterConfig>
  smoothingConfigs: Record<string, SmoothingConfig>
  resampleConfigs: Record<string, ResampleConfig>
  regressionConfigs: RegressionConfig[]
}

/**
 * Inverse of `buildDataProcessingConfigsFromState`: fan the stored
 * `data_processing_configs` array back out into the provider state shapes.
 *
 * Old charts predate this field entirely (`dataProcessingConfigs` is nullish in
 * the schema), so a missing array loads as "no processing configured" rather
 * than throwing.
 */
export const loadDataProcessingConfigsFromChart = (
  chart: StandaloneChart
): LoadedDataProcessingConfigs => {
  const thresholdFilters: Record<string, ThresholdFilterConfig> = {}
  const smoothingConfigs: Record<string, SmoothingConfig> = {}
  const resampleConfigs: Record<string, ResampleConfig> = {}
  const regressionConfigs: RegressionConfig[] = []

  for (const config of chart.dataProcessingConfigs ?? []) {
    if (config.type === 'threshold_filter') {
      thresholdFilters[config.sensorId] = config
    } else if (config.type === 'smoothing') {
      smoothingConfigs[config.sensorId] = config
    } else if (config.type === 'resample') {
      resampleConfigs[config.sensorId] = config
    } else {
      regressionConfigs.push(config)
    }
  }

  return {
    thresholdFilters,
    smoothingConfigs,
    resampleConfigs,
    regressionConfigs,
  }
}

/**
 * Inverse of `serializeRulerConfigs`.
 *
 * The stored form has no pixel span by design, so the live `Ruler` is rebuilt
 * with both pixel fields at 0. That is not a guess at geometry: `RulerOverlay`
 * derives the rendered pixels from `startXValue`/`endXValue` via `valToPos`
 * whenever uPlot is mounted and only falls back to these fields before the plot
 * exists (RulerOverlay.tsx:360-374), at which point nothing is drawn yet.
 *
 * Old charts predate `ruler_configs` (nullish in the schema), so a missing array
 * loads as "no rulers" rather than throwing.
 */
export const loadRulerConfigsFromChart = (chart: StandaloneChart): Ruler[] =>
  (chart.rulerConfigs ?? []).map((config) => ({
    id: config.id,
    startXValue: config.startXValue,
    endXValue: config.endXValue,
    startPixelOnOverlay: 0,
    endPixelOnOverlay: 0,
    selectedSensorIds: config.selectedSensorIds,
    isMinimized: config.isMinimized ?? false,
    panelOffsetY: config.panelOffsetY ?? 0,
  }))

/** Inverse of `serializePointAnnotationConfigs`. */
export const loadPointAnnotationsFromChart = (
  chart: StandaloneChart
): PointAnnotation[] =>
  (chart.pointAnnotationConfigs ?? []).map((config) => ({
    id: config.id,
    anchorTimestamp: config.anchorTimestamp,
    anchorYRatio: config.anchorYRatio,
    sensorId: config.sensorId,
  }))

interface DateConfigState {
  dateMode: DateMode
  timeRange: TimeRange
  liveTail: boolean
  relativeStart: { value?: number; unit: ChartTimeUnit }
  relativeEnd: { isNow: boolean; value?: number; unit: ChartTimeUnit }
  cadenceSeconds: number
}

export const loadDateConfigFromChart = (
  chart: StandaloneChart,
  defaultCadenceSeconds: number
): DateConfigState | null => {
  if (chart.dateConfig?.mode === 'absolute') {
    // `===` on two Date objects compares references, so the original form of
    // this check could never be true and every absolute chart loaded as
    // 'custom'. Compare by value instead.
    const preset: TimeRange['preset'] = dayjs(
      chart.dateConfig.startDate
    ).isSame(dayjs().startOf('year'), 'day')
      ? 'ytd'
      : 'custom'

    return {
      dateMode: 'absolute',
      timeRange: {
        from: chart.dateConfig.startDate!.toISOString(),
        to: chart.dateConfig.endDate!.toISOString(),
        preset,
      },
      liveTail: false,
      relativeStart: { value: 7, unit: 'days' },
      relativeEnd: { isNow: true, value: 0, unit: 'days' },
      cadenceSeconds: defaultCadenceSeconds,
    }
  } else if (chart.dateConfig?.mode === 'relative') {
    const { value: startValue, unit: startUnit } =
      chart.dateConfig.startDateLookback!
    const endLookBack = chart.dateConfig.endDateLookback
    const cadence =
      chart.dateConfig.updateCadenceSeconds ?? defaultCadenceSeconds

    const now = dayjs()
    const relativeEnd = endLookBack
      ? { isNow: false, value: endLookBack.value, unit: endLookBack.unit }
      : {
          isNow: true,
          value: 0,
          unit: 'days' as ChartTimeUnit,
        }

    return {
      dateMode: 'relative',
      timeRange: {
        from: now.subtract(startValue, startUnit).toISOString(),
        to: endLookBack
          ? now.subtract(endLookBack.value, endLookBack.unit).toISOString()
          : now.toISOString(),
      },
      liveTail: true,
      relativeStart: { value: startValue, unit: startUnit },
      relativeEnd,
      cadenceSeconds: cadence,
    }
  }

  return null
}
